import { MouseEventCoords, TimeRange, Timestamp, useGraphCtx } from '@mi-front/core';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { theme } from '../../../styles';
import { TimeLabel } from '../label';
import { Marker } from '../marker';
import { StreamEl } from '../stream-el';
import { ClickableArea } from './ClickableArea';
export * from './ClickableGraphAreaNoRaf';

/**
 * Represents a time range where clicks should be disabled.
 */
export interface UnclickableRange extends TimeRange<number> {
  readonly length: number;
}

export interface Props {
  readonly onTsClick: (ts: Timestamp) => void;
  readonly onHover?: (ts: Timestamp | null) => void;
  readonly onBlur?: () => void;
  readonly showHoverCursor?: boolean;
  readonly hoverCursorColor?: string;
  readonly cursorType?: 'dot' | 'line';
  readonly showHoverTimeLabel?: boolean;
  readonly tz?: string;
  readonly unclickableRanges?: readonly UnclickableRange[];
}

ClickableGraphArea.defaultProps = {
  showHoverCursor: true,
  cursorType: 'line',
  showHoverTimeLabel: true,
};

/**
 * ClickableGraphArea component that handles mouse interactions on a graph area.
 *
 * **Why useCallback is used for handlers:**
 *
 * **Bug Fix**: There was a bug that caused infinite re-renders and "Maximum update depth exceeded"
 * errors. The root cause was that event handlers were created as inline arrow functions on every
 * render, causing the `ClickableArea` child component's `useEffect` (which depends on these
 * callbacks) to re-run continuously, triggering state updates that caused more re-renders.
 *
 * The event handlers (onHoverHandler, onClickHandler, onBlurHandler) are now wrapped in
 * `useCallback` with empty dependency arrays to ensure they maintain stable function
 * references across re-renders. This fixes the infinite render loop because:
 *
 * 1. **Prevents infinite render loops**: The `ClickableArea` child component's `useEffect`
 *    depends on these callback props. By keeping the callbacks stable, the effect only runs
 *    when truly necessary, not on every render.
 *
 * 2. **Stable references**: By using `useCallback` with empty deps, the callbacks are only
 *    created once and remain stable, preventing unnecessary re-renders of child components.
 *
 * 3. **Accessing changing values via refs**: Since the callbacks don't depend on `scale` or
 *    `p` props, we use refs (`scaleRef`, `propsRef`) to access the latest values. This allows
 *    the callbacks to stay stable while still using current data.
 *
 * 4. **Deferred parent updates**: The `onHoverHandler` uses `requestAnimationFrame` to defer
 *    parent state updates, breaking synchronous render cascades that could cause performance
 *    issues or render loops.
 */
export function ClickableGraphArea(p: Props) {
  const { width, height, scale, range } = useGraphCtx();
  // Optimization #2: Consolidate related state to reduce state updates
  const [hoverState, setHoverState] = useState<{
    ts: number | null;
    isOverUnclickable: boolean;
  }>({ ts: null, isOverUnclickable: false });

  const scaleRef = useRef(scale);
  const propsRef = useRef(p);
  const prevHoverTsRef = useRef<number | null>(null);
  const rafIdRef = useRef<number | null>(null);

  // Optimization #9: Destructure props for better readability
  const {
    showHoverCursor = true,
    hoverCursorColor,
    cursorType = 'line',
    showHoverTimeLabel = true,
    tz,
    unclickableRanges,
    onHover,
    onTsClick,
    onBlur,
  } = p;

  scaleRef.current = scale;
  propsRef.current = p;

  // Optimization #1: Cleanup requestAnimationFrame on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  /**
   * Optimization #6 & #7: Extract timestamp calculation with proper type safety.
   * Converts mouse coordinates to a timestamp using the current scale.
   */
  const coordsToTimestamp = useCallback((coords: MouseEventCoords): Timestamp => {
    return Number(scaleRef.current.invert(coords.x)) as Timestamp;
  }, []);

  /**
   * Checks if a timestamp falls within any of the un-clickable ranges.
   * Optimization #4: This callback is stable (empty deps), so it doesn't need to be
   * in dependency arrays of other callbacks that use it.
   */
  const isTimestampUnclickable = useCallback((ts: number): boolean => {
    const ranges = propsRef.current.unclickableRanges;
    if (!ranges || ranges.length === 0) {
      return false;
    }
    return ranges.some((range) => ts >= range.from && ts <= range.to);
  }, []);

  /**
   * Optimization #10: Extract parent hover notification to reduce code duplication
   * and simplify the hover handler logic.
   */
  const notifyParentHover = useCallback((ts: Timestamp | null) => {
    if (!propsRef.current.onHover) return;

    // Cancel any pending callback
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
    }

    // Defer parent update to next animation frame to break synchronous render cascade
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      propsRef.current.onHover!(ts);
    });
  }, []);

  const onHoverHandler = useCallback(
    (coords: MouseEventCoords) => {
      const ts = Number(scaleRef.current.invert(coords.x));
      const isUnclickable = isTimestampUnclickable(ts);

      // Only update state if the timestamp has actually changed
      if (prevHoverTsRef.current === ts) return;
      prevHoverTsRef.current = ts;

      // Optimization #10: Simplified logic flow with early returns
      if (isUnclickable) {
        // Don't show hover cursor over un-clickable ranges
        setHoverState({ ts: null, isOverUnclickable: true });
        // Still notify parent but with null to indicate un-clickable area
        notifyParentHover(null);
        return;
      }

      // Handle clickable case
      setHoverState({ ts, isOverUnclickable: false });
      notifyParentHover(ts as Timestamp);
    },
    [isTimestampUnclickable, notifyParentHover],
  );

  const onClickHandler = useCallback(
    (coords: MouseEventCoords) => {
      const ts = coordsToTimestamp(coords);

      // Check if timestamp is in an un-clickable range
      if (isTimestampUnclickable(ts)) {
        return; // Ignore clicks in un-clickable ranges
      }

      setHoverState({ ts: null, isOverUnclickable: false });
      propsRef.current.onTsClick(ts);
    },
    [coordsToTimestamp, isTimestampUnclickable],
  );

  const onBlurHandler = useCallback((_coords: MouseEventCoords) => {
    // Cancel any pending hover rAF so a deferred onHover(ts) cannot run after blur and leave the thumbnail visible
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    setHoverState({ ts: null, isOverUnclickable: false });
    propsRef.current.onBlur && propsRef.current.onBlur();
  }, []);

  // Optimization #3: Memoize hover cursor rendering to avoid unnecessary re-renders
  const hoverCursor = useMemo(() => {
    if (!showHoverCursor || !hoverState.ts) return null;

    const color = hoverCursorColor || theme.aqua;

    return (
      <>
        {cursorType === 'line' ? (
          <Marker ts={hoverState.ts} color={color} />
        ) : (
          <StreamEl ts={hoverState.ts} checkOverflow zIndex={100} position="center" noEvents>
            <div
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: color,
                position: 'absolute',
                opacity: 0.5,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }}
            />
          </StreamEl>
        )}
        {showHoverTimeLabel && (
          <StreamEl
            ts={hoverState.ts}
            checkOverflow
            offsetX={10}
            zIndex={100}
            position="center"
            noEvents
          >
            <TimeLabel ts={hoverState.ts} tz={tz} size="md" />
          </StreamEl>
        )}
      </>
    );
  }, [showHoverCursor, hoverState.ts, hoverCursorColor, cursorType, showHoverTimeLabel, tz]);

  // Optimization #8: More robust key generation using template literal for better uniqueness
  const areaKey = `${range.from}-${range.to}`;

  // key prop required to reset the area
  return (
    <React.Fragment>
      <ClickableArea
        key={areaKey}
        height={height}
        width={width}
        onHover={onHoverHandler}
        onClick={onClickHandler}
        onBlur={onBlurHandler}
        cursor={hoverState.isOverUnclickable ? 'not-allowed' : 'pointer'}
      />
      {hoverCursor}
    </React.Fragment>
  );
}
